import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { canonicalLink, facebookFeedMeta, pathFromMatch } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { LetsTalkForm } from "@/components/LetsTalkForm";

import comm from "@/assets/original/stock-commercial.jpg";
import marineBoat from "@/assets/marine-canvas-boat.jpg";
import playgroundShade from "@/assets/playground-shade.jpg";
import porch from "@/assets/original/stock-porch.jpg";

// ---------------------------------------------------------------------------
// Filterable "View Our Work" gallery. Each project is tagged with a category
// the user requested (commercial, marine, playground, residential porch) so
// visitors can narrow the grid by the kind of job they're shopping for.
// ---------------------------------------------------------------------------
type Category = "commercial" | "marine" | "playground" | "porch";

interface Project {
  src: string;
  alt: string;
  title: string;
  category: Category;
}

const projects: Project[] = [
  {
    src: comm,
    alt: "Commercial storefront awning installation",
    title: "Commercial Storefront Awning",
    category: "commercial",
  },
  {
    src: marineBoat,
    alt: "Marine canvas boat cover on a docked vessel",
    title: "Marine Canvas Boat Cover",
    category: "marine",
  },
  {
    src: playgroundShade,
    alt: "Tension shade sail over a school playground",
    title: "Playground Tension Shade Sail",
    category: "playground",
  },
  {
    src: porch,
    alt: "Residential porch shade with retractable awning",
    title: "Residential Porch Shade",
    category: "porch",
  },
];

const FILTERS: { id: Category | "all"; label: string }[] = [
  { id: "all", label: "All Work" },
  { id: "commercial", label: "Commercial" },
  { id: "marine", label: "Marine" },
  { id: "playground", label: "Playground" },
  { id: "porch", label: "Porch" },
];

export const Route = createFileRoute("/view-our-work")({
  // Surfaces our day-to-day Facebook work, so use the shared facebookFeedMeta
  // helper to guarantee absolute OG/Twitter image URLs for social previews.
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: facebookFeedMeta({
      title: "Our Work — Commercial, Marine & Residential | Texas Made Shade",
      description: "Filter Texas Made Shade & Covers projects by category — commercial awnings, marine canvas, playground shade sails, and residential porch shade across Texas.",
      image: comm,
      socialTitle: "View Our Work | Texas Made Shade & Covers",
      socialDescription:
        "Filterable project grid: commercial, marine, playground, and porch shade work across Texas.",
    }, ctx),
    scripts: pageHeadScripts({
      path: "/view-our-work",
      name: "Our Work — Commercial, Marine & Residential",
      description: "Filter Texas Made Shade & Covers projects by category — commercial awnings, marine canvas, playground shade sails and residential porch shade across Texas.",
    }),
  }),
  component: ViewOurWorkPage,
});

function ViewOurWorkPage() {
  const [active, setActive] = useState<Category | "all">("all");
  const filtered = useMemo(
    () => (active === "all" ? projects : projects.filter((p) => p.category === active)),
    [active],
  );

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="relative bg-navy-deep py-20 text-white">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-white">★ VIEW OUR WORK</div>
            <h1 className="mt-3 font-display text-5xl font-bold md:text-7xl">
              Built. Welded. Installed.
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-white/80">
              Filter our portfolio by the type of project you have in mind — commercial storefronts,
              marine canvas, playground shade sails, or residential porch shade.
            </p>
          </div>
        </section>

        {/* Filter bar */}
        <section className="border-y-4 border-navy-deep bg-cream">
          <div
            className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-5 lg:px-8"
            role="group"
            aria-label="Project category filters"
          >
            {FILTERS.map((f) => {
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActive(f.id)}
                  className={
                    "inline-flex items-center gap-2 border-2 border-navy-deep px-4 py-2 font-display text-xs uppercase tracking-[0.2em] transition " +
                    (isActive
                      ? "bg-navy-deep text-white"
                      : "bg-white text-navy-deep hover:bg-navy-deep hover:text-white")
                  }
                >
                  {f.label}
                </button>
              );
            })}
            <div className="ml-auto font-display text-xs uppercase tracking-[0.2em] text-navy-deep/60">
              {filtered.length} {filtered.length === 1 ? "project" : "projects"}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="bg-cream py-16" aria-labelledby="work-grid-heading">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <h2 id="work-grid-heading" className="sr-only">
              Project gallery — filtered by {active === "all" ? "all categories" : active}
            </h2>

            {filtered.length === 0 ? (
              <p className="py-16 text-center font-display text-lg uppercase tracking-wider text-navy-deep/60">
                No projects in this category yet — check back soon.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((p) => (
                  <figure
                    key={p.src}
                    className="group relative overflow-hidden border-2 border-navy-deep bg-navy-deep"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={p.src}
                        alt={p.alt}
                        loading="lazy"
                        decoding="async"
                        width={800}
                        height={600}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <figcaption className="flex items-center justify-between gap-2 bg-cream p-4">
                      <div>
                        <div className="font-display text-[10px] uppercase tracking-[0.25em] text-red">
                          {p.category}
                        </div>
                        <div className="mt-1 font-display text-base font-bold uppercase leading-tight text-navy-deep">
                          {p.title}
                        </div>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            <div className="mt-10 text-center">
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 border-2 border-navy-deep bg-white px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                See full project gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <QuoteCTA />
        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}
